import type {
  LookalikeCluster,
  LookalikePersona,
  LookalikeSchema,
} from '@repo/contracts';

import { describeCluster } from './cluster-labels';
import { toFeatureVector } from './features';
import { kMeans } from './kmeans';
import { standardize } from './linalg';
import { mulberry32 } from './prng';
import { sampleLookalikes } from './sample';

export { applyWeightUpdates } from './apply-weights';
export { describeCluster } from './cluster-labels';
export { FEATURE_NAMES, SAVVINESS_SCORE, toFeatureVector } from './features';
export { kMeans } from './kmeans';
export { standardize } from './linalg';
export { projectTo2D } from './pca';
export {
  gaussian,
  mulberry32,
  type RandomSource,
  uniform,
  weightedPick,
} from './prng';
export { sampleLookalikes } from './sample';

/** Clusters into three groups: `viz-1`/`viz-2`/`viz-3` in `apps/web`'s
 *  Tailwind config are the only categorical slots validated for a scatter
 *  plot's all-pairs colour comparison (see `apps/web/design.md`'s reference
 *  data-viz palette) — a fourth would put two colours on screen together that
 *  fail contrast/colourblind-safety checks. Fixed rather than configurable
 *  for that reason: it isn't a product choice, it's a constraint the chart
 *  already has. */
const CLUSTER_COUNT = 3;

/**
 * The whole of "materialise a lookalike population from its spec": sample,
 * then cluster and label the sample. One function because both steps need
 * the same seeded stream in the same order — see `sampleLookalikes` and
 * `kMeans`'s own docs on why this is deterministic in `sampleSeed` alone.
 *
 * `specId` only prefixes each row's `id` (`${specId}-${index}`, see
 * `LookalikePersona`'s own doc) — it plays no role in sampling or clustering,
 * so two specs sampled with the same seed and schema produce identically
 * *valued* populations, just under different ids.
 */
export function materializeLookalikes(
  specId: string,
  schema: LookalikeSchema,
  sampleSeed: number,
  count: number,
): { clusters: LookalikeCluster[]; lookalikes: LookalikePersona[] } {
  const random = mulberry32(sampleSeed);
  const sampled = sampleLookalikes(schema, random, count);

  const vectors = sampled.map((lookalike) =>
    toFeatureVector(schema, lookalike),
  );
  const { standardized } = standardize(vectors);
  const { assignments } = kMeans(standardized, CLUSTER_COUNT, random);

  const lookalikes: LookalikePersona[] = sampled.map((lookalike, i) => ({
    ...lookalike,
    cluster: assignments[i]!,
    id: `${specId}-${lookalike.index}`,
  }));

  const clusters: LookalikeCluster[] = Array.from(
    { length: CLUSTER_COUNT },
    (_, index) => {
      const members = lookalikes.filter(
        (lookalike) => lookalike.cluster === index,
      );
      return {
        index,
        label: describeCluster(members, lookalikes),
        size: members.length,
      };
    },
  ).filter((cluster) => cluster.size > 0);

  return { clusters, lookalikes };
}
